/**
 * Admin modal for editing the customer-facing login reminder announcement
 * (the gold banner on every customer dashboard). English + Spanish text.
 * Changes only affect display text — no notifications are ever sent.
 */
import { useCallback, useEffect, useState } from 'react';
import { X, Megaphone, Loader2, Save, CheckCircle2, AlertCircle } from 'lucide-react';

import { adminApi } from '@/lib/api';

export default function AdminAnnouncementModal({ onClose }) {
  const [textEn, setTextEn] = useState('');
  const [textEs, setTextEs] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [flash, setFlash] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await adminApi.getAnnouncement();
      setTextEn(data.text_en || '');
      setTextEs(data.text_es || '');
    } catch (err) {
      setFlash({ type: 'error', msg: err?.detail || 'Failed to load announcement.' });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (saving) return;
    setSaving(true);
    setFlash(null);
    try {
      await adminApi.saveAnnouncement(textEn, textEs);
      setFlash({ type: 'success', msg: 'Saved. Customers will see this on their next dashboard load.' });
    } catch (err) {
      setFlash({ type: 'error', msg: err?.detail || 'Save failed.' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
      data-testid="admin-announcement-modal"
      onClick={(e) => { if (e.target === e.currentTarget && !saving) onClose(); }}
    >
      <div className="bg-white rounded-2xl shadow-xl max-w-lg w-full overflow-hidden max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between px-5 py-4 border-b border-[#EDE5D4]">
          <div className="flex items-center gap-2">
            <Megaphone className="h-5 w-5 text-[#0B1930]" />
            <div>
              <h2 className="font-heading uppercase tracking-wide text-base text-[#0B1930]">
                Login Announcement
              </h2>
              <p className="text-[11px] text-[#4B5563]">
                Shown on every customer dashboard. Leave both empty to hide it.
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="text-[#4B5563] hover:text-[#0B1930] disabled:opacity-40"
            data-testid="admin-announcement-close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="px-5 py-4 overflow-y-auto flex-1">
          {loading ? (
            <div className="text-sm text-[#4B5563] flex items-center gap-2">
              <Loader2 className="h-4 w-4 animate-spin" />
              Loading current announcement…
            </div>
          ) : (
            <>
              {flash && (
                <div
                  className={`mb-3 rounded-xl px-3 py-2 text-sm flex items-start gap-2 ${
                    flash.type === 'success'
                      ? 'bg-emerald-50 border border-emerald-200 text-emerald-900'
                      : 'bg-red-50 border border-red-200 text-red-800'
                  }`}
                  data-testid="admin-announcement-flash"
                >
                  {flash.type === 'success'
                    ? <CheckCircle2 className="h-4 w-4 mt-0.5 shrink-0" />
                    : <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />}
                  <span>{flash.msg}</span>
                </div>
              )}
              <div className="space-y-4">
                <div>
                  <label className="block text-[11px] uppercase tracking-wide text-[#4B5563] mb-1">
                    Announcement (English)
                  </label>
                  <textarea
                    rows={3}
                    value={textEn}
                    onChange={(e) => setTextEn(e.target.value)}
                    maxLength={600}
                    className="w-full rounded-xl bg-white border border-[#EDE5D4] px-3 py-2 text-sm text-[#0B1930] focus:outline-none focus:border-[#C8A46B]"
                    data-testid="admin-announcement-en"
                  />
                </div>
                <div>
                  <label className="block text-[11px] uppercase tracking-wide text-[#4B5563] mb-1">
                    Announcement (Español)
                  </label>
                  <textarea
                    rows={3}
                    value={textEs}
                    onChange={(e) => setTextEs(e.target.value)}
                    maxLength={600}
                    className="w-full rounded-xl bg-white border border-[#EDE5D4] px-3 py-2 text-sm text-[#0B1930] focus:outline-none focus:border-[#C8A46B]"
                    data-testid="admin-announcement-es"
                  />
                </div>
              </div>
            </>
          )}
        </div>

        <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-[#EDE5D4] bg-[#FAF7F0]">
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="rounded-full px-4 py-2 text-xs font-bold uppercase tracking-wide text-[#0B1930] hover:bg-[#EDE5D4] transition-colors disabled:opacity-60"
            data-testid="admin-announcement-cancel"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={save}
            disabled={loading || saving}
            className="inline-flex items-center gap-2 rounded-full bg-[#0B1930] hover:bg-[#111A2E] text-white px-4 py-2 text-xs font-bold uppercase tracking-wide transition-colors disabled:opacity-60"
            data-testid="admin-announcement-save"
          >
            {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
            Save
          </button>
        </div>
      </div>
    </div>
  );
}
